import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Filter, Calendar, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState } from "@/components/EmptyState";
import { formatCourseDate, toLocalDate } from "@/lib/dates";

// Estados em destaque (atalhos visuais)
const QUICK_STATES = [
  { value: "todos", label: "Todos" },
  { value: "SC", label: "SC" },
  { value: "MG", label: "MG" },
  { value: "PR", label: "PR" },
  { value: "DF", label: "DF" },
  { value: "BA", label: "BA" },
  { value: "outros", label: "Outros" },
];
const QUICK_STATES_UF = ["SC", "MG", "PR", "DF", "BA"];

// Filtro rápido de mês (tira de botões)
const MONTH_LABELS = [
  "Jan", "Fev", "Mar", "Abr", "Mai", "Jun",
  "Jul", "Ago", "Set", "Out", "Nov", "Dez",
];
const MONTH_NAMES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];
const STORAGE_KEY = "calendario_quick_state";
const MONTH_STORAGE_KEY = "calendario_quick_month";

export default function Calendario() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ area: "todos", estado: "", cidade: "", modality: "todos", month: "todos" });
  const [openFilter, setOpenFilter] = useState(false);
  const [quickState, setQuickState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || "todos"; }
    catch { return "todos"; }
  });
  const [quickMonth, setQuickMonth] = useState(() => {
    try { return localStorage.getItem(MONTH_STORAGE_KEY) || "todos"; }
    catch { return "todos"; }
  });

  // Persistir filtros rápidos entre sessões
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, quickState); } catch { /* ignore */ }
  }, [quickState]);
  useEffect(() => {
    try { localStorage.setItem(MONTH_STORAGE_KEY, quickMonth); } catch { /* ignore */ }
  }, [quickMonth]);

  // Carrega TODOS os cursos uma vez; filtros são aplicados no client (mais responsivo).
  const load = (f = filters) => {
    setLoading(true);
    const params = {};
    if (f.area && f.area !== "todos") params.area = f.area;
    if (f.estado) params.estado = f.estado;
    if (f.cidade) params.cidade = f.cidade;
    if (f.modality && f.modality !== "todos") params.modality = f.modality;
    api.get("/courses", { params })
      .then(r => setItems(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Verifica se um curso "acontece" no mês selecionado (intersecção entre [start_date, end_date] e [mês])
  const courseInMonth = (c, monthIdx) => {
    if (monthIdx === "todos") return true;
    if (!c.start_date || !c.end_date) return false;
    try {
      // Usa toLocalDate para evitar shift de fuso horário (bug clássico
      // "1 dia a menos" que afetava a intersecção de mês).
      const sd = toLocalDate(c.start_date);
      const ed = toLocalDate(c.end_date);
      if (!sd || !ed) return false;
      const m = Number(monthIdx); // 0..11
      // Aceita se start ou end estão no mês, OU se o intervalo do curso cobre o mês inteiro
      // Estratégia: percorre o intervalo e verifica se algum dia cai no mês alvo
      const cursor = new Date(sd.getFullYear(), sd.getMonth(), 1);
      const end = new Date(ed.getFullYear(), ed.getMonth(), 1);
      while (cursor.getTime() <= end.getTime()) {
        if (cursor.getMonth() === m) return true;
        cursor.setMonth(cursor.getMonth() + 1);
      }
      return false;
    } catch {
      return false;
    }
  };

  // Aplicação dos filtros rápidos no cliente
  const visibleItems = useMemo(() => {
    let list = items;
    // estado
    if (quickState !== "todos") {
      if (quickState === "outros") {
        list = list.filter(c => {
          const uf = (c.estado || "").toUpperCase().trim();
          return !QUICK_STATES_UF.includes(uf);
        });
      } else {
        list = list.filter(c => (c.estado || "").toUpperCase().trim() === quickState);
      }
    }
    // mês
    if (quickMonth !== "todos") {
      list = list.filter(c => courseInMonth(c, quickMonth));
    }
    return list;
  }, [items, quickState, quickMonth]);

  const apply = () => {
    setQuickMonth(filters.month);
    load(filters);
    setOpenFilter(false);
  };
  const clear = () => {
    const c = { area: "todos", estado: "", cidade: "", modality: "todos", month: "todos" };
    setFilters(c);
    setQuickMonth("todos");
    load(c);
    setOpenFilter(false);
  };

  return (
    <div className="space-y-4" data-testid="calendario-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-semibold text-xl text-[hsl(var(--primary))]">Calendário de Cursos</h1>
        <Drawer open={openFilter} onOpenChange={setOpenFilter}>
          <DrawerTrigger asChild>
            <Button variant="outline" size="sm" data-testid="calendar-filter-button">
              <Filter className="w-4 h-4 mr-1" /> Filtros
            </Button>
          </DrawerTrigger>
          <DrawerContent>
            <DrawerHeader><DrawerTitle>Filtrar cursos</DrawerTitle></DrawerHeader>
            <div className="px-5 pb-6 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Área</Label>
                  <Select value={filters.area} onValueChange={(v) => setFilters(f => ({ ...f, area: v }))}>
                    <SelectTrigger><SelectValue placeholder="Todas" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todas</SelectItem>
                      <SelectItem value="legislativo">Legislativo</SelectItem>
                      <SelectItem value="licitacoes">Licitações</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Modalidade</Label>
                  <Select value={filters.modality} onValueChange={(v) => setFilters(f => ({ ...f, modality: v }))}>
                    <SelectTrigger><SelectValue placeholder="Todas" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todas</SelectItem>
                      <SelectItem value="presencial">Presencial</SelectItem>
                      <SelectItem value="online">Online</SelectItem>
                      <SelectItem value="hibrido">Híbrido</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Estado (UF)</Label>
                  <Input maxLength={2} value={filters.estado} onChange={(e) => setFilters(f => ({ ...f, estado: e.target.value.toUpperCase() }))} placeholder="UF" /></div>
                <div><Label>Cidade</Label>
                  <Input value={filters.cidade} onChange={(e) => setFilters(f => ({ ...f, cidade: e.target.value }))} placeholder="Cidade" /></div>
                <div className="col-span-2"><Label>Mês</Label>
                  <Select value={filters.month} onValueChange={(v) => setFilters(f => ({ ...f, month: v }))}>
                    <SelectTrigger data-testid="filter-month-select"><SelectValue placeholder="Todos os meses" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos os meses</SelectItem>
                      {MONTH_NAMES.map((m, i) => (
                        <SelectItem key={i} value={String(i)}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={clear} className="flex-1">Limpar</Button>
                <Button onClick={apply} className="flex-1" data-testid="calendar-filter-apply">Aplicar</Button>
              </div>
            </div>
          </DrawerContent>
        </Drawer>
      </div>

      {/* Filtro rápido de estado (tira de botões) */}
      <div
        className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 no-scrollbar"
        data-testid="quick-state-filter"
      >
        {QUICK_STATES.map(s => {
          const active = quickState === s.value;
          return (
            <button
              key={s.value}
              type="button"
              onClick={() => setQuickState(s.value)}
              data-testid={`quick-state-${s.value}`}
              className={[
                "shrink-0 px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors border",
                active
                  ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))] shadow-sm"
                  : "bg-white text-[hsl(var(--primary))] border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]",
              ].join(" ")}
              aria-pressed={active}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Filtro rápido de mês (tira de botões) */}
      <div
        className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 no-scrollbar"
        data-testid="quick-month-filter"
      >
        <button
          type="button"
          onClick={() => setQuickMonth("todos")}
          data-testid="quick-month-todos"
          className={[
            "shrink-0 px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors border",
            quickMonth === "todos"
              ? "bg-[hsl(var(--accent))] text-white border-[hsl(var(--accent))] shadow-sm"
              : "bg-white text-[hsl(var(--foreground))] border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]",
          ].join(" ")}
          aria-pressed={quickMonth === "todos"}
        >
          Todos
        </button>
        {MONTH_LABELS.map((m, i) => {
          const active = quickMonth === String(i);
          return (
            <button
              key={i}
              type="button"
              onClick={() => setQuickMonth(String(i))}
              data-testid={`quick-month-${i}`}
              className={[
                "shrink-0 px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors border",
                active
                  ? "bg-[hsl(var(--accent))] text-white border-[hsl(var(--accent))] shadow-sm"
                  : "bg-white text-[hsl(var(--foreground))] border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]",
              ].join(" ")}
              aria-pressed={active}
            >
              {m}
            </button>
          );
        })}
      </div>

      {/* Resultado */}
      {loading ? (
        <Card className="rounded-xl"><CardContent className="p-4 text-sm text-muted-foreground">Carregando…</CardContent></Card>
      ) : visibleItems.length === 0 ? (
        <EmptyState
          title={
            quickMonth !== "todos"
              ? `Nenhum curso em ${MONTH_NAMES[Number(quickMonth)]}${quickState !== "todos" ? ` (${quickState === "outros" ? "outros estados" : quickState})` : ""}`
              : quickState === "todos"
              ? "Nenhum curso encontrado"
              : `Nenhum curso em ${quickState === "outros" ? "outros estados" : quickState}`
          }
          description="Ajuste os filtros ou tente novamente em breve."
        />
      ) : (
        visibleItems.map(c => {
          return (
            <Link key={c.id} to={`/calendario/${c.id}`} data-testid={`course-card-${c.id}`}>
              <Card className="rounded-xl hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="secondary" className="capitalize">{c.area}</Badge>
                    <Badge variant="outline">{c.workload_hours}h</Badge>
                    <Badge variant="outline" className="capitalize">{c.modality}</Badge>
                  </div>
                  <h3 className="font-display font-semibold text-base text-[hsl(var(--primary))]">{c.title}</h3>
                  {c.professor && (
                    <p className="text-xs text-muted-foreground mt-0.5">Prof. {c.professor}</p>
                  )}
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{c.description}</p>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                    <span className="flex items-center gap-1 text-[hsl(var(--accent))] font-medium">
                      <Calendar className="w-3.5 h-3.5" /> {formatCourseDate(c.start_date)} – {formatCourseDate(c.end_date)}
                    </span>
                    <span className="flex items-center gap-1 text-muted-foreground">
                      <MapPin className="w-3.5 h-3.5" /> {c.cidade}/{c.estado}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })
      )}
    </div>
  );
}
